import React from 'react';

import { ChevronLeft, ChevronRight } from 'lucide-react';

import { Label } from '@/components/atoms/label';
import { Button } from '@/components/atoms/new-button';
import { Fact } from '@/types';

interface LabellerFactViewerProps {
  facts: Fact[];
  currentFactIndex: number;
  onPrevious: () => void;
  onNext: () => void;
  children?: React.ReactNode;
}

export const LabellerFactViewer = ({ facts, currentFactIndex, onPrevious, onNext, children }: LabellerFactViewerProps) => {
  const currentFact = facts[currentFactIndex];

  return (
    <div className="w-1/2 overflow-y-auto border-r border-gray-200 p-6">
      {/* Navigation Header */}
      <div className="mb-4 flex items-center justify-between">
        <h3 className="text-lg font-semibold">
          Fact {currentFactIndex + 1} of {facts.length}
        </h3>
        <div className="flex gap-2">
          <Button size="sm" variant="outline" onClick={onPrevious} disabled={currentFactIndex === 0}>
            <ChevronLeft className="h-4 w-4" />
          </Button>
          <Button size="sm" variant="outline" onClick={onNext} disabled={currentFactIndex === facts.length - 1}>
            <ChevronRight className="h-4 w-4" />
          </Button>
        </div>
      </div>

      {currentFact && (
        <div className="space-y-4">
          {/* Additional content (e.g., group selection) */}
          {children}

          {/* Fact Details */}
          <div>
            <Label className="text-sm font-medium text-gray-600">Description</Label>
            <p className="mt-1 text-sm">{currentFact.description || 'No description'}</p>
          </div>

          {currentFact.author && (
            <div>
              <Label className="text-sm font-medium text-gray-600">Author</Label>
              <p className="mt-1 text-sm">{currentFact.author}</p>
            </div>
          )}

          {currentFact.factDateText && (
            <div>
              <Label className="text-sm font-medium text-gray-600">Date</Label>
              <p className="mt-1 text-sm">{currentFact.factDateText}</p>
            </div>
          )}

          {currentFact.sourceText && (
            <div>
              <Label className="text-sm font-medium text-gray-600">Source</Label>
              <p className="mt-1 text-sm">{currentFact.sourceText}</p>
            </div>
          )}

          {currentFact.topic && (
            <div>
              <Label className="text-sm font-medium text-gray-600">Topic</Label>
              <p className="mt-1 text-sm">{currentFact.topic}</p>
            </div>
          )}

          {currentFact.significance && (
            <div>
              <Label className="text-sm font-medium text-gray-600">Significance</Label>
              <p className="mt-1 text-sm">{currentFact.significance}</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
};
